import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import type { OrderingSettings } from "@luckymenu/core";
import { useCallback, useMemo, useState } from "react";
import { type OrderingProblem, orderingProblemOf } from "../ordering/problems";

/**
 * More > Ordering settings (MG-09): QR ordering, auto-accept, service
 * requests and asking for the diner's name. Each switch saves on its own and
 * moves at once; if the server refuses, it moves back and `problem` names the
 * field and the reason. Reading needs any role; changing needs
 * `settings.manage`.
 */
export function useOrderingSettings() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const queryKey = useMemo(() => trpc.staff.ordering.getSettings.queryKey(), [trpc]);
	const query = useQuery(trpc.staff.ordering.getSettings.queryOptions());
	const [problem, setProblem] = useState<{
		field: keyof OrderingSettings;
		problem: OrderingProblem;
	} | null>(null);
	const mutation = useMutation(
		trpc.staff.ordering.updateSettings.mutationOptions({
			onMutate: async (patch) => {
				await queryClient.cancelQueries({ queryKey });
				const before = queryClient.getQueryData<OrderingSettings>(queryKey);
				queryClient.setQueryData<OrderingSettings>(queryKey, (old) =>
					old ? { ...old, ...patch } : old,
				);
				return { before };
			},
			onError: (error, patch, context) => {
				const before = (context as { before?: OrderingSettings } | undefined)?.before;
				const field = Object.keys(patch)[0] as keyof OrderingSettings;
				// Only the refused field goes back: another switch may have saved meanwhile.
				if (before)
					queryClient.setQueryData<OrderingSettings>(queryKey, (old) =>
						old ? { ...old, [field]: before[field] } : old,
					);
				setProblem({ field, problem: orderingProblemOf(error) });
			},
			onSuccess: (settings) => {
				// With another switch still saving, its optimistic value stays until it answers.
				const mutationKey = trpc.staff.ordering.updateSettings.mutationKey();
				if (queryClient.isMutating({ mutationKey }) <= 1)
					queryClient.setQueryData(queryKey, settings);
			},
		}),
	);
	const set = useCallback(
		<K extends keyof OrderingSettings>(field: K, value: OrderingSettings[K]) => {
			setProblem(null);
			mutation.mutate({ [field]: value } as Partial<OrderingSettings>);
		},
		[mutation],
	);
	return {
		status: query.data
			? ("ready" as const)
			: query.isError
				? ("error" as const)
				: ("loading" as const),
		retry: () => void query.refetch(),
		settings: query.data ?? null,
		set,
		problem,
		clearProblem: () => setProblem(null),
	};
}
